import { useState, useEffect } from 'react'
import { getAdminUpdates, addUpdate, updateUpdate, deleteUpdate, restoreUpdate } from '../services/dbService'
import { formatTimestamp } from '../utils/helpers'

export default function AdminUpdates() {
  const [updates, setUpdates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  
  // Form State
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({ title: '', image: '', content: '', slug: '' })
  const [formLoading, setFormLoading] = useState(false)

  const fetchUpdates = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAdminUpdates()
      setUpdates(data)
    } catch (err) {
      setError('Failed to fetch updates. ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUpdates()
  }, [])

  const resetForm = () => {
    setFormData({ title: '', image: '', content: '', slug: '' })
    setEditingId(null)
    setShowForm(false)
  }

  const handleEdit = (update) => {
    setFormData({
      title: update.title || '',
      image: update.image || '',
      content: update.content || '',
      slug: update.slug || ''
    })
    setEditingId(update.id)
    setShowForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.image || !formData.content) {
      alert('Title, Image, and Content are required.')
      return
    }
    
    setFormLoading(true)
    setError('')
    try {
      if (editingId) {
        await updateUpdate(editingId, formData)
      } else {
        await addUpdate(formData)
      }
      resetForm()
      await fetchUpdates()
    } catch (err) {
      setError('Failed to save update. ' + err.message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to soft-delete "${title}"?`)) {
      try {
        await deleteUpdate(id, false)
        await fetchUpdates()
      } catch (err) {
        setError('Failed to delete update. ' + err.message)
      }
    }
  }

  const handleRestore = async (id) => {
    try {
      await restoreUpdate(id)
      await fetchUpdates()
    } catch (err) {
      setError('Failed to restore update. ' + err.message)
    }
  }

  if (loading) return <div>Loading updates...</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.2rem', color: '#7a8fa6', margin: 0 }}>Manage Updates ({updates.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true) }} className="btn-gold" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
          + Add Update
        </button>
      </div>

      {error && <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: 12, borderRadius: 8, marginBottom: 20 }}>{error} <button onClick={fetchUpdates} style={{ background: 'transparent', border: '1px solid currentColor', color: 'inherit', padding: '2px 8px', borderRadius: 4, marginLeft: 10, cursor: 'pointer' }}>Retry</button></div>}

      {showForm && (
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: 24, borderRadius: 12, marginBottom: 30 }}>
          <h3 style={{ marginTop: 0, marginBottom: 20 }}>{editingId ? 'Edit Update' : 'New Update'}</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <input type="text" placeholder="Title *" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
              <input type="text" placeholder="Slug (optional, auto-generated)" value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} style={inputStyle} />
            </div>
            
            <input type="url" placeholder="Image URL *" value={formData.image} onChange={e => setFormData({...formData, image: e.target.value})} required style={inputStyle} />
            
            {formData.image && (
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', color: '#7a8fa6', display: 'block', marginBottom: 5 }}>Image Preview:</span>
                <img src={formData.image} alt="Preview" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image+URL' }} style={{ height: 100, objectFit: 'cover', borderRadius: 8 }} />
              </div>
            )}

            <textarea placeholder="Content (Max 500 chars) *" maxLength={500} rows={5} value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} required style={inputStyle} />
            
            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <button type="submit" disabled={formLoading} className="btn-gold" style={{ padding: '10px 24px' }}>
                {formLoading ? 'Saving...' : 'Save Update'}
              </button>
              <button type="button" onClick={resetForm} disabled={formLoading} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '10px 24px', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {updates.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: 40, borderRadius: 12, textAlign: 'center', color: '#7a8fa6' }}>
          No updates found.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {updates.map(u => (
            <div key={u.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, overflow: 'hidden', border: u.isDeleted ? '1px dashed #ef4444' : '1px solid transparent', opacity: u.isDeleted ? 0.7 : 1 }}>
              <img src={u.image} alt={u.title} onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error' }} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
              <div style={{ padding: 16 }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>{u.title}</h4>
                <div style={{ fontSize: '0.75rem', color: '#7a8fa6', marginBottom: 12 }}>{formatTimestamp(u.createdAt?.seconds ? u.createdAt : { seconds: Date.now() / 1000 })} | Slug: {u.slug}</div>
                {u.isDeleted && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: 10 }}>[DELETED - TRASH]</div>}
                
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={() => handleEdit(u)} style={actionBtnStyle}>Edit</button>
                  {u.isDeleted ? (
                    <button onClick={() => handleRestore(u.id)} style={{...actionBtnStyle, color: '#4ade80', borderColor: 'rgba(34,197,94,0.3)'}}>Restore</button>
                  ) : (
                    <button onClick={() => handleDelete(u.id, u.title)} style={{...actionBtnStyle, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)'}}>Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: 'white', fontFamily: "'Inter', sans-serif"
}

const actionBtnStyle = {
  flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 600
}
